<template>
  <ProgressCircular v-if="loading" />
  <div v-else>
    <form
      novalidate
      ref="updateHotelForm"
      :disabled="disabled"
      @submit.prevent="() => {}"
    >
      <v-row no-gutters>
        <v-col cols="8">
          <v-card max-width="1200" flat color="transparent">
            <v-card-title class="mb-10">
              <FormHeader
                :loading="loading"
                :disabled="disabled"
                :link="'page-hotels'"
                :title="data.title ? `${data.title} отель` : `Создать отель`"
              />
            </v-card-title>
            <v-card-text>
              <v-container>
                <HeaderSteppers
                  :tabs="$options.tabsNav"
                  :is-new="isNew"
                  :rules="getRulesTab"
                  tooltipMessages="Необходимо создать отель!"
                >
                  <template #0>
                    <v-card-text>
                      <PageContent height="65">
                        <InputTitleSlug
                          :is-loading="loading || disabled"
                          :title="fields.title"
                          :title-error="getFieldErrorMessage('title')"
                          :slug="fields.slug"
                          :slug-error="getFieldErrorMessage('slug')"
                          :isSlugEnabled="!isNew"
                          @change:title="fields.title = $event"
                          @change:slug="fields.slug = $event"
                          @input:slug="$v.fields.slug.$touch"
                        />
                        <v-row>
                          <v-col cols="12">
                            <MediaFilePicker
                              :label="'Миниатюра'"
                              :multiple="false"
                              :file-type="$options.mediaTypesByKeys.IMAGE"
                              :value="fields.thumbnailId"
                              @change="fields.thumbnailId = $event"
                            />
                          </v-col>
                          <v-col cols="12">
                            <MediaFilePicker
                              id="images"
                              :label="'Изображения*'"
                              :file-type="$options.mediaTypesByKeys.IMAGE"
                              :value="fields.images"
                              @change="fields.images = $event"
                            />
                            <ErrorMessage :message="getFieldErrorMessage('images')" />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <span>Адрес*</span>
                            <LocationAutocomplete
                              id="address"
                              :address="fields.address"
                              :loading="loading || disabled"
                              :error-messages="getFieldErrorMessage('address')"
                              @set-location="fields.address = $event"
                              @update-location-part="updateLocation"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                          <span>Описание* (<TranslationIcon />)</span>
                          <QuillEditorVue
                            id="description"
                            :cols="12"
                            :title="''"
                            :loading="loading"
                            :disabled="disabled"
                            :isShowErrorMessage="true"
                            :errorText="getFieldErrorMessage('description')"
                            :content="fields.description"
                            @input="fields.description = $event"
                            @onChangeContent="$emit('change:descriptionForMobile', { fields, $event })"
                          />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                          <span>Краткое описание* (<TranslationIcon />)</span>
                          <QuillEditorVue
                            id="shortDescription"
                            :loading="loading"
                            :disabled="disabled"
                            :cols="12"
                            :title="''"
                            :isShowErrorMessage="true"
                            :errorText="getFieldErrorMessage('shortDescription')"
                            :content="fields.shortDescription"
                            light-mode
                            @input="fields.shortDescription = $event"
                            @onChangeContent="$emit('change:shortDescriptionForMobile', {fields, $event})"
                          />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <span>Количество звезд</span>
                            <v-text-field
                              v-model.number="fields.stars"
                              outlined
                              :disabled="disabled"
                              :loading="loading"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <SeoSettings
                              :data="data"
                              :loading="loading || disabled"
                              @on-input-seo="fields = Object.assign({}, fields, $event)"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="6">
                            <span>Время заезда*</span>
                            <TimePicker
                              id="checkInTime"
                              step="1"
                              :loading="loading"
                              :disabled="disabled"
                              :error-messages="getFieldErrorMessage('checkInTime')"
                              v-model="fields.checkInTime"
                            />
                          </v-col>
                          <v-col cols="6">
                            <span>Время выезда*</span>
                            <TimePicker
                              step="1"
                              :loading="loading"
                              :disabled="disabled"
                              :error-messages="getFieldErrorMessage('checkOutTime')"
                              v-model="fields.checkOutTime"
                            />
                          </v-col>
                        </v-row>
                        <v-card-actions>
                          <BtnNext
                            :loading="loading"
                            :disabled="disabled"
                            :title="`${isNew ? 'Создать' : 'Сохранить'} ${tabs[
                              activeTab
                            ].title.toLowerCase()}`"
                            @nextClick="nextBtnClickHandler(activeTab)"
                          />
                        </v-card-actions>
                      </PageContent>
                    </v-card-text>
                  </template>
                  <template #1>
                    <v-card-text>
                      <PageContent height="65">
                        <FormHotelRoomTypes
                          :room-types="fields.roomTypes || []"
                          :amenities="amenities"
                          :error-messages="$v.fields.roomTypes.$each || {}"
                          :is-valid-room-types="
                            $v.fields.roomTypes.$dirty && $v.fields.roomTypes.$invalid
                          "
                          @create="createRoomType"
                          @option="roomTypeOptionHandler"
                          @change:descriptionForMobile="changeDescriptionForMobileInRoomType($event)"
                        />
                      </PageContent>
                    </v-card-text>
                    <v-card-actions>
                      <BtnNext
                        :loading="loading"
                        :disabled="disabled"
                        :title="`${isNew ? 'Создать' : 'Сохранить'} ${tabs[
                          activeTab
                        ].title.toLowerCase()}`"
                        @nextClick="nextBtnClickHandler(activeTab)"
                      />
                    </v-card-actions>
                  </template>
                  <template #2>
                    <v-card-text>
                      <PageContent height="65">
                        <FormHotelRooms
                          :rooms="fields.rooms || []"
                          :room-types="data.roomTypes || []"
                          :error-messages="$v.fields.rooms.$each || {}"
                          @create="createRoom"
                          @option="roomOptionHandler"
                        />
                      </PageContent>
                    </v-card-text>
                  </template>
                </HeaderSteppers>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="4">
          <ManageSidePanel
            isShowSortOrder
            :is-new="!data.id"
            :created-at="data.createdAt"
            :updated-at="data.updatedAt"
            :published-at="data.publishedAt || ''"
            :author="data.author"
            :is-loading="loading"
            :is-submit-disabled="disabled"
            :language="language"
            :translations="fields.translations"
            :entity-id="data.id"
            :sortOrder="fields.sortOrder"
            :entity-type="'hotel'"
            @delete="$emit('delete-hotel')"
            @cancel="$router.push({ name: 'page-hotels' })"
            @submit="submit"
            @update-translations="Object.assign(fields.translations, $event)"
            @language-update="$emit('language-update', $event)"
            @update-sort-order="fields.sortOrder = Number($event)"
          >
            <template #info-append>
              <v-row align="center">
                <v-col cols="6"> Резерваций: </v-col>
                <v-col cols="6">
                  <router-link
                    :to="{
                      name: 'page-orders-booking',
                      query: { id: data.id }
                    }"
                  >
                    {{ reservationsCount }}
                  </router-link>
                </v-col>
              </v-row>
              <StatusPicker
                align="center"
                cols="12"
                title="Статус:"
                :disabled="disabled"
                :loading="loading"
                :status="fields.status"
                @status:change="fields.status = $event"
              />
              <v-row v-if="!!fields.detailsEntityId" align="center">
                <v-col cols="6">
                  <span>Посмотреть инф. о партнере</span>
                </v-col>
                <v-col cols="6">
                  <v-btn icon color="primary" @click="$refs.profileForm.open()">
                    <v-icon>mdi-card-account-details</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row v-if="!!fields.detailsEntityId" align="center">
                <v-col cols="6">
                  <span>Посмотреть общий доступ</span>
                </v-col>
                <v-col cols="6">
                  <v-btn
                    icon
                    link
                    color="primary"
                    :to="redirectToSharedAccess"
                    :loading="loading"
                    :disabled="disabled"
                    ><v-icon>mdi-account-group</v-icon></v-btn
                  >
                </v-col>
              </v-row>
            </template>
            <template #body>
              <v-card flat>
                <v-card-subtitle class="grey--text"> Связи </v-card-subtitle>
                <v-card-text>
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>Город*</span>
                      <v-select
                        id="cityId"
                        chips
                        :disabled="disabled"
                        :loading="loading"
                        :error-messages="getFieldErrorMessage('cityId')"
                        outlined
                        dense
                        item-value="id"
                        item-text="title"
                        clearable
                        :items="cities"
                        v-model="fields.cityId"
                      >
                        <template #selection="{ item }">
                          <v-chip>
                            {{ item.title }}
                            <v-btn
                              icon
                              x-small
                              @click.stop="
                                fields.cityId = null
                                fields.placeId = null
                              "
                            >
                              <v-icon> mdi-close </v-icon>
                            </v-btn>
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>Тип*</span>
                      <v-select
                        chips
                        outlined
                        dense
                        id="typeId"
                        item-value="id"
                        item-text="label"
                        clearable
                        :disabled="disabled"
                        :loading="loading"
                        :error-messages="getFieldErrorMessage('typeId')"
                        :items="types"
                        v-model="fields.typeId"
                      >
                        <template #selection="{ item }">
                          <v-chip>
                            {{ item.label }}
                            <v-btn icon x-small @click.stop="fields.typeId = null">
                              <v-icon> mdi-close </v-icon>
                            </v-btn>
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                  <v-row no-gutters v-if="isCertificateNumberRequired">
                    <v-col cols="12">
                      <span>Номер сертификата*</span>
                      <v-text-field
                        outlined
                        dense
                        id="certificateNumber"
                        :disabled="disabled"
                        :loading="loading"
                        :error-messages="getFieldErrorMessage('certificateNumber')"
                        v-model.trim="fields.certificateNumber"
                      />
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>Удобства</span>
                      <v-select
                        chips
                        :disabled="disabled"
                        :loading="loading"
                        outlined
                        dense
                        item-value="id"
                        item-text="label"
                        multiple
                        :items="facilities"
                        v-model="fields.facilities"
                      >
                        <template #selection="{ item }">
                          <v-chip>
                            {{ item.label }}
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </template>
          </ManageSidePanel>
        </v-col>
      </v-row>
      <ProfileForm
        v-if="fields.detailsEntityId"
        ref="profileForm"
        :profileId="fields.detailsEntityId"
        :detailsEntityType="fields.detailsEntityType"
      />
    </form>
  </div>
</template>
<script>
import { mixForm } from '@/utils/form.mix'
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'

import { defaultDateFormat } from '@/configs/date.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'
import { contentLocales } from '@/configs/locales.js'
import hotelTabs from '@/configs/hotelTabs'

import validationSchemaRules from '@/pages/hotel/form/validations'

import LocationAutocomplete from '@/components/location-autocomplete'
import ManageSidePanel from '@/components/manage-side-panel'
import MediaFilePicker from '@/components/media-file-picker'
import SeoSettings from '@/components/seo-settings'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import FormHeader from '@/components/form-header'
import InputTitleSlug from '@/components/inputs/InputTitleSlug'
import FormHotelRoomTypes from '@/pages/hotel/form/roomTypes/FormHotelRoomTypes'
import FormHotelRooms from '@/pages/hotel/form/rooms/FormHotelRooms'
import QuillEditorVue from '@/components/quill-editor'
import BtnNext from './btnNext'
import StatusPicker from '@/components/status-picker'
import ProfileForm from '@/components/profile-form'
import HeaderSteppers from '@/components/header-steppers'
import ErrorMessage from '@/components/error-messages'
import TimePicker from '@/components/inputs/TimePicker'
import PageContent from '@/components/page-content/PageContent.vue'
import ProgressCircular from '@/components/progress-circular'

class RoomType {
  title = null
  description = null
  maxGuests = null
  maxAdults = null
  maxChildren = null
  basePrice = null
  images = []
  amenities = []
}

class Room {
  number = null
  roomTypeId = null
  floor = null
}

export default {
  name: 'FormHotel',
  contentLocales,
  tabsNav: hotelTabs,
  mediaTypesByKeys: mediaTypesByKeys(),
  defaultDateFormat,
  mixins: [mixForm, validationTranslations, prepareTranslations],
  validationSchemaRules,
  validations () {
    return this.validationsScheme
  },
  components: {
    PageContent,
    FormHotelRooms,
    FormHotelRoomTypes,
    InputTitleSlug,
    TranslationIcon,
    ManageSidePanel,
    LocationAutocomplete,
    MediaFilePicker,
    SeoSettings,
    FormHeader,
    QuillEditorVue,
    BtnNext,
    StatusPicker,
    ProfileForm,
    TimePicker,
    HeaderSteppers,
    ErrorMessage,
    ProgressCircular
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    cities: {
      type: Array,
      default: () => []
    },
    amenities: {
      type: Array,
      default: () => []
    },
    types: {
      type: Array,
      default: () => []
    },
    facilities: {
      type: Array,
      default: () => []
    },
    language: {
      type: String,
      default: null
    },
    isNew: {
      type: Boolean,
      default: false
    },
    tabs: {
      type: Array,
      default: () => null
    },
    currentTab: {
      type: Number,
      default: 0
    },
    reservationsCount: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: 0,
      fields: {
        rooms: [],
        roomTypes: [],
        typeId: null,
        title: null,
        description: null,
        shortDescription: null,
        thumbnailId: null,
        stars: null,
        slug: undefined,
        images: [],
        facilities: [],
        cityId: null,
        address: {
          lon: null,
          lat: null,
          address: null
        },
        checkInTime: null,
        checkOutTime: null,
        seoOgImage: null,
        seoMetaTitle: null,
        seoMetaKeywords: null,
        seoMetaDescription: null,
        seoDisallowIndex: null,
        seoRelCanonical: null,
        status: null,
        certificateNumber: null,
        translations: { ru: { isEnabled: true } },
        sortOrder: null,
        detailsEntityId: null,
        detailsEntityType: null
      },
      hotelTypesCertificateNumberRequired: [
        'hotelTypeHotel',
        'hotelTypeHostel',
        'hotelTypeRecreationCenter'
      ]
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.fields.images = data.images.map(image => image.id)
      this.fields.facilities = data.facilities.map(facility => facility.id)
      this.fields.cityId = data.city?.id
      this.fields.thumbnailId = data?.thumbnail?.id || null
      this.fields.typeId = data.type?.id
      // roomTypes
      this.populateRoomTypes(data.roomTypes)
      // rooms
      this.populateRooms(data.rooms)
    }
  },
  computed: {
    isCertificateNumberRequired () {
      const selectedHotelType = this.types.filter(type => type.id === this.fields.typeId)
      if (selectedHotelType.length) {
        return this.hotelTypesCertificateNumberRequired.includes(
          selectedHotelType[0].value
        )
      } else {
        return false
      }
    },
    validationsScheme () {
      return this.$options.validationSchemaRules({
        id: this.$props.data?.id,
        ctx: this,
        isNew: this.isNew,
        certificateRequired: this.isCertificateNumberRequired
      })
    },
    validationRulesVars () {
      return { id: this.data?.id, ctx: this, isNew: this.isNew }
    },
    getRoomTypesRules () {
      return this?.$v?.fields?.roomTypes?.$each || {}
    },
    getRulesTab () {
      const rules = Object.keys(this.$v?.fields).filter(rule => !rule.includes('$'))
      const hotelsRules = rules
        .filter(rule => rule !== 'roomTypes' && rule !== 'rooms')
        .reduce((acc, rule) => {
          if (this.$v?.fields[rule]?.$dirty && this.$v?.fields[rule]?.$invalid) {
            if (!acc.includes('invalid')) acc.push('invalid')
          }
          return acc
        }, [])
      const roomTypes = []
      const rooms = []

      if (this.$v?.fields?.roomTypes?.$dirty && this.$v?.fields?.roomTypes?.$invalid) {
        if (!roomTypes.includes('invalid')) roomTypes.push('invalid')
      }

      if (this.$v?.fields?.rooms?.$dirty && this.$v?.fields?.rooms?.$invalid) {
        if (!rooms.includes('invalid')) rooms.push('invalid')
      }
      return { hotel: hotelsRules, roomTypes, rooms }
    }
  },
  methods: {
    createRoomType () {
      this.fields.roomTypes.push(new RoomType())
    },
    changeDescriptionForMobileInRoomType ({ itemIndex, descForMob }) {
      this.fields.roomTypes[itemIndex].descriptionForMobile = descForMob
    },
    createRoom () {
      this.fields.rooms.push(new Room())
    },
    roomOptionHandler (optionData = {}) {
      const { option, item, itemIndex } = optionData
      // delete option
      if (option.value === 'delete') {
        if (item.id) this.$emit('remove-room', item.id)
        else this.fields.rooms.splice(itemIndex, 1)
      }
    },
    roomTypeOptionHandler (optionData = {}) {
      const { option, item, itemIndex } = optionData
      // delete option
      if (option.value === 'delete') {
        if (item.id) this.$emit('remove-room-type', item.id)
        else this.fields.roomTypes.splice(itemIndex, 1)
      }
    },
    populateRoomTypes (list = []) {
      this.fields.roomTypes = list.map(roomType => {
        const roomTypePayload = { ...roomType }
        roomTypePayload.images = roomTypePayload.images.map(image =>
          image.id ? image.id : image
        )
        roomTypePayload.amenities = roomTypePayload.amenities.map(amenity =>
          amenity.id ? amenity.id : amenity
        )
        delete roomTypePayload.updatedAt
        delete roomTypePayload.createdBy
        delete roomTypePayload.createdAt
        delete roomTypePayload.hotel
        return roomTypePayload
      })
    },
    populateRooms (list = []) {
      this.fields.rooms = list.map(room => {
        const roomPayload = { ...room }
        roomPayload.roomTypeId = roomPayload.roomType.id
        delete roomPayload.roomType
        return roomPayload
      })
    },
    updateLocation ({ value, fieldName }) {
      this.fields.address[fieldName] = value
    },
    getFieldErrorMessage (fieldName, msg = 'Обязательное поле') {
      if (this.$v.fields[fieldName] === undefined) return ''
      const v = this.$v.fields[fieldName]
      if (fieldName === 'address') {
        if (
          !this.fields.address?.address ||
          !this.fields.address?.lon ||
          !this.fields.address?.lat
        ) {
          return v.$invalid && v.$dirty ? { address: {}, lat: {}, lon: {} } : []
        } else {
          return {}
        }
      }
      return v.$invalid && v.$dirty ? [msg] : []
    },
    async submit () {
      const payload = await this.preparePayload()
      if (!payload) return
      this.$emit('submit', payload)
    },
    async nextBtnClickHandler (activeTab) {
      const { value } = this.tabs[activeTab] || null
      if (!value) return
      if (value === 'hotel' || value === 'roomTypes') {
        const payload = await this.preparePayload()
        if (!payload) return
        this.$emit('onwards', { activeTab, payload })
      }
    },
    async preparePayload () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }

      const payload = this.composeFormData(this.fields)

      if (
        !payload?.roomTypes?.length &&
        !payload?.rooms?.length &&
        payload.status === 'PUBLISHED'
      ) {
        this.$errorMsg('Нельзя опубликовать отель без номеров!')
        return
      }

      payload.seoOgImageId = payload.seoOgImage?.id
      delete payload.seoOgImage
      return payload
    }
  }
}
</script>
